import { DrawerHeader } from '../../../../../drawer/DrawerHeader';
import { graphql } from '../../../../../gql';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { isLinksJson, isOldLinksJson } from '../../../../../form/links/LinksFormInput';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { DrawerWrapper } from '../../../../../drawer/DrawerWrapper';
import { useSnackbar } from '../../../../../store/snackbar';
import { LinkType, LinksFormInput } from '../../../../../form/links/LinksFormInput';

const activityLinksQuery = graphql(`
	query journeyDetail_listActivityLinks($activityId: UUID!) {
		activityLinks: getActivity(by: { id: $activityId }) {
			links
		}
	}
`);

const updateLinksMutation = graphql(`
	mutation links_updateLinks($activityId: UUID!, $links: Json!) {
		updateActivity(by: { id: $activityId }, data: { links: { values: $links } }) {
			ok
			errorMessage
		}
	}
`);

export const linksEditDrawerSchema = z.object({
	activityId: z.string(),
});

export const LinksEditDrawer: FC<z.infer<typeof linksEditDrawerSchema>> = ({ activityId }) => {
	const intl = useIntl();
	const { data, isLoading } = useApiQuery(activityLinksQuery, { variables: { activityId } });
	const { mutate: updateLinks } = useApiMutation(updateLinksMutation);
	const initialData = (() => {
		if (isLinksJson(data?.activityLinks?.links)) {
			return data.activityLinks.links.values;
		} else if (isOldLinksJson(data?.activityLinks?.links)) {
			return data.activityLinks.links.values.map((link) => ({ link, text: undefined }));
		}
		return null;
	})();
	const { openSnackbar } = useSnackbar();

	const update = useCallback(
		(links: LinkType[]) => {
			updateLinks(
				{ activityId, links },
				{
					onSuccess: (result) => {
						mutationResponseHandler({
							result: result.updateActivity.ok,
							toastMessages: {
								success: intl.formatMessage({ id: 'widget.links.update.success' }),
								error: intl.formatMessage({ id: 'widget.links.update.error' }),
							},
							errorMessage: result.updateActivity.errorMessage,
							openSnackbar,
						});
					},
				},
			);
		},
		[updateLinks, activityId, intl, openSnackbar],
	);

	return (
		<DrawerWrapper>
			<DrawerHeader title={intl.formatMessage({ id: 'journey.detail.widget.links.title' })} />
			<LinksFormInput onChange={update} value={initialData} size={'medium'} isLoading={isLoading} />
		</DrawerWrapper>
	);
};
